import React from 'react';
import { FiCheckCircle, FiArrowRight, FiVideo, FiPlay, FiEye, FiTrendingUp, FiUsers, FiAward } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const YouTubeAdsPage = () => {
  useDocumentTitle(
    'YouTube Ads Management | Run by the Best YouTube Ads Agency in India',
    'Promote your brand with a targeted youtube ad that\'s simple and effective. Managed by the best youtube ads agency in india for strong visibility and results.'
  );

  const features = [
    {
      icon: FiVideo,
      title: "Video Ads",
      description: "Skippable and non\u2011skippable video campaigns that introduce your offer clearly and push viewers to take action."
    },
    {
      icon: FiPlay,
      title: "Bumper Ads",
      description: "Short 6\u2011second story\u2011driven ads designed to quickly remind people of your brand and offers."
    },
    {
      icon: FiEye,
      title: "Discovery Ads",
      description: "Ads that show in YouTube search results and \"watch next\" sections so people can choose to watch your video like regular content."
    },
    {
      icon: FiTrendingUp,
      title: "Video Production Support",
      description: "Help with scripts, hooks, storyboards and basic editing guidance so your videos feel natural and not like generic TV ads."
    },
    {
      icon: FiUsers,
      title: "Audience Targeting",
      description: "Target by interests, keywords, topics, placements and custom audiences to reach people who are most likely to care."
    },
    {
      icon: FiAward,
      title: "YouTube SEO",
      description: "Optimise titles, descriptions and thumbnails so your videos can rank better and keep working for you even after the ads stop."
    }
  ];

  const pricingPlans = [
    {
      name: "Starter",
      price: "₹25,000",
      period: "/month",
      description: "Perfect for businesses new to YouTube advertising",
      features: [
        "Ad Spend: Up to ₹50,000/month",
        "1 Video Campaign",
        "Basic Video Editing (1 ad)",
        "Audience Research",
        "Campaign Setup & Management",
        "Monthly Performance Report",
        "Email Support"
      ],
      highlighted: false
    },
    {
      name: "Growth",
      price: "₹50,000",
      period: "/month",
      description: "Ideal for scaling YouTube video advertising",
      features: [
        "Ad Spend: Up to ₹2,00,000/month",
        "3 Video Campaigns",
        "Professional Video Creation (3 ads)",
        "Advanced Audience Targeting",
        "Multiple Ad Formats",
        "A/B Testing",
        "Bi-weekly Reports",
        "Phone + Email Support"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For large-scale YouTube advertising campaigns",
      features: [
        "Unlimited Ad Spend",
        "Unlimited Campaigns",
        "Full Video Production Team",
        "Custom Audience Development",
        "YouTube Channel Management",
        "Influencer Collaboration",
        "Weekly Performance Calls",
        "Dedicated Account Manager"
      ],
      highlighted: false
    }
  ];

  const whyChooseUs = [
    {
      title: "Performance‑focused video team",
      description: "Campaigns are built to drive results—leads, sales and sign‑ups—not just views and impressions."
    },
    {
      title: "Big‑agency thinking, lean setup",
      description: "You get strategy and ideas you would expect from india's top advertising agencies, but with faster execution and direct access to the team."
    },
    {
      title: "Long‑term brand building",
      description: "Creative, targeting and analytics are aligned so your YouTube presence looks as strong as work coming from india's best advertising agencies, while still staying practical and budget‑aware."
    }
  ];

  const faqs = [
    {
      question: "Do I need ready videos or can you help?",
      answer: "You can bring your own videos, or we can guide you on scripts, hooks and simple production options that fit your budget."
    },
    {
      question: "How much should I spend on YouTube ads?",
      answer: "It depends on your industry and goals. We normally start with a test budget to find winning audiences and creatives, then scale once results are stable."
    },
    {
      question: "Can YouTube ads work for lead generation, not just branding?",
      answer: "Yes. With the right targeting, offers and landing pages, YouTube can be a strong channel for leads and sales, not just awareness."
    },
    {
      question: "How do you measure success?",
      answer: "We track views, clicks, watch time, leads and sales so you can see both brand impact and hard numbers."
    },
    {
      question: "Can you work with my existing YouTube channel?",
      answer: "Absolutely. We review your current videos, playlists and analytics, then build a plan that uses what you already have and fills the gaps."
    }
  ];

  const [openFaqIndex, setOpenFaqIndex] = React.useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#FF0000] to-[#CC0000] text-white py-20">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              YouTube Advertising That Turns Views Into Customers
            </h1>
            <div className="space-y-2 mb-6 text-base md:text-lg text-gray-100">
              <p>Searching for a <span className="font-semibold">youtube ad strategy</span> that actually brings leads, not just views?</p>
              <p>Tired of scrolling through every '<span className="font-semibold">best youtube ads agency in india</span>' list and still not finding the right fit?</p>
              <p>Work with a team that gives you big‑agency thinking without the big‑agency headache people expect from india's top advertising agencies and india's best advertising agencies.</p>
            </div>
            <p className="text-lg md:text-xl mb-8 text-gray-100">
              Mirani Marketing helps brands run YouTube campaigns that reach the right people with the right message and a clear call to action. From scripting and creatives to targeting and tracking, everything is done with performance in mind.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#pricing"
                className="bg-white text-[#FF0000] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
              >
                View Pricing
              </a>
              <a
                href="#contact"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#FF0000] transition-colors inline-block"
              >
                Get Free Consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-8 text-gray-900">
              What is YouTube Advertising?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="text-lg leading-relaxed mb-6">
                YouTube advertising lets your brand show video ads before, during or around the videos your audience is already watching. You pay only when someone watches long enough or interacts, depending on the format.
              </p>
              <p className="text-lg leading-relaxed">
                Done right, a <span className="font-semibold">youtube ad</span> can build awareness, explain your product in seconds and send ready‑to‑buy visitors to your website or landing page.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Our YouTube Advertising Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow"
                >
                  <div className="w-12 h-12 bg-[#FF0000] bg-opacity-10 rounded-lg flex items-center justify-center mb-4 text-[#FF0000]">
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            Transparent Pricing
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Choose the perfect plan for your business needs. All plans include expert management and optimization.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`rounded-2xl p-8 transform hover:scale-105 transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-[#FF0000] text-white shadow-2xl scale-105 border-4 border-[#FF0000]'
                    : 'bg-white border-2 border-gray-200 shadow-lg hover:shadow-2xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="text-center mb-4">
                    <span className="bg-white text-[#FF0000] px-4 py-1 rounded-full text-sm font-semibold">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <h3
                  className={`text-2xl font-bold mb-2 ${
                    plan.highlighted ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span
                    className={`text-4xl font-bold ${
                      plan.highlighted ? 'text-white' : 'text-[#FF0000]'
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={`text-lg ${
                      plan.highlighted ? 'text-gray-100' : 'text-gray-600'
                    }`}
                  >
                    {plan.period}
                  </span>
                </div>
                <p
                  className={`mb-6 ${
                    plan.highlighted ? 'text-gray-100' : 'text-gray-600'
                  }`}
                >
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle
                        className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'text-white' : 'text-[#FF0000]'
                        }`}
                      />
                      <span
                        className={
                          plan.highlighted ? 'text-gray-100' : 'text-gray-700'
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className={`block text-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                    plan.highlighted
                      ? 'bg-white text-[#FF0000] hover:bg-gray-100'
                      : 'bg-[#FF0000] text-white hover:bg-[#CC0000]'
                  }`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Why Choose Mirani Marketing for YouTube Ads?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-[#FF0000] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <FiCheckCircle className="w-5 h-5 text-[#FF0000]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex justify-between items-center"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-[#FF0000] text-2xl">
                    {openFaqIndex === index ? '−' : '+'}
                  </span>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 py-4 bg-white">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-16 bg-gradient-to-br from-[#FF0000] to-[#CC0000] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Launch Your YouTube Ads?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-100">
            If you're comparing the best youtube ads agency in india or exploring options among india's top advertising agencies, add Mirani Marketing to your list. Let's build a youtube ad strategy that feels human, tells your story and drives real results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-white text-[#FF0000] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
            >
              Get Free Consultation
            </a>
            <a
              href="/contact"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#FF0000] transition-colors inline-block"
            >
              Call / WhatsApp Your Team
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default YouTubeAdsPage;

